import { DateFormat, calculateTimeDifferenceBetweenDates, formatDateTimeWithLocale } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { DefaultPrimaryButton, DefaultSecondaryButton, Spinner } from '@frontend/components/common';
import { CommonMessage, useLocale } from '@frontend/lang';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedPudoTransaction, TransactionStateName } from '@frontend/transaction/types';
import { LuCalendar, LuCheck, LuDoorClosed, LuUser } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { KioskNavigationFooter } from '../../components/kiosk-navigation-footer.component';
import { KioskNavigationHeader } from '../../components/kiosk-navigation-header.component';
import { useBulkMoveTransactionsList } from './use-bulk-move-transactions-list';

const ID = 'bulk-move-transactions';

const formatTrackingNumber = (id: string): string => {
    if (id.length <= 12) return id;
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
};

const BulkMoveSelectableItem = (props: { transaction: ExtendedPudoTransaction; selected: boolean; onToggle: () => void }) => {
    const { locale } = useLocale();
    const isPickUpPending = props.transaction.state === TransactionStateName.TRANSACTION_PICK_UP_PENDING;
    const waitingFrom = isPickUpPending ? props.transaction.dropped_off_timestamp : props.transaction.creation_timestamp;

    return (
        <button
            type='button'
            id={`${ID}-item-${props.transaction.id}`}
            className={`border-border-default bg-background-surface flex w-full flex-col items-start justify-start gap-1.5 rounded-xl border p-4 py-3 text-left font-medium ${
                props.selected ? 'outline-brand-primary outline outline-2 -outline-offset-2' : ''
            }`}
            onClick={props.onToggle}
            aria-pressed={props.selected}>
            <div className='flex w-full items-center justify-between gap-2'>
                <div className='flex min-w-0 items-center gap-2'>
                    <span
                        className={`flex size-6 shrink-0 items-center justify-center rounded border ${
                            props.selected ? 'border-brand-primary bg-brand-primary text-background-surface' : 'border-border-default'
                        }`}
                        aria-hidden>
                        {props.selected ? <LuCheck className='size-4' /> : null}
                    </span>
                    <p className='text-foreground-default truncate leading-snug font-semibold'>
                        {formatTrackingNumber(props.transaction.tracking_number ?? '')}
                    </p>
                </div>
                <div className='flex shrink-0 items-center gap-2'>
                    <TransactionStateBadge state={props.transaction.state} />
                    {waitingFrom ? (
                        <DefaultBadge
                            label={
                                <>
                                    {CommonMessage.OBJECTS.TRANSACTION.WAITING_FOR} {calculateTimeDifferenceBetweenDates(waitingFrom, new Date().toISOString())}
                                </>
                            }
                            styles={{ container: { color: isPickUpPending ? 'bg-status-info' : 'bg-status-warning' } }}
                        />
                    ) : null}
                </div>
            </div>
            <div className='text-brand-muted flex w-full flex-col gap-1 pl-8'>
                <div className='flex items-center gap-2'>
                    <LuUser className='size-5 shrink-0' />
                    <p className='min-w-0 flex-1'>
                        {CommonMessage.OBJECTS.TRANSACTION.SENDER}: {props.transaction.senders?.[0]?.name || CommonMessage.OBJECTS.UNKNOWN.DEFAULT}
                    </p>
                </div>
                <div className='flex items-center gap-2'>
                    <LuDoorClosed className='size-5 shrink-0' />
                    <p>
                        {props.transaction.slot
                            ? `SLOT ${props.transaction.slot.name}`
                            : CommonMessage.ERRORS.UNKNOWN_ENTITY(CommonMessage.OBJECTS.SLOT.DEFAULT)}
                    </p>
                </div>
                <div className='flex items-center gap-2'>
                    <LuCalendar className='size-5 shrink-0' />
                    <p>
                        {CommonMessage.OBJECTS.COMMON_FIELDS.CREATED_AT}{' '}
                        {formatDateTimeWithLocale(new Date(props.transaction.creation_timestamp), locale, DateFormat.DD_MM_YYYY_HH_MM)}
                    </p>
                </div>
            </div>
        </button>
    );
};

const BulkMoveTransactionsList = () => {
    const viewProps = useBulkMoveTransactionsList();

    return (
        <div
            id={ID}
            className='bg-background-default flex size-full flex-col items-center justify-start overflow-hidden'>
            <KioskNavigationHeader
                title={CommonMessage.DETAIL.MANAGE(CommonMessage.OBJECTS.SPOT.DISTRI_SPOT)}
                onBack={viewProps.handleBack}
            />
            <div className='flex size-full flex-col items-start justify-start gap-3 overflow-y-auto px-6 py-4'>
                <div className='flex w-full flex-col gap-1'>
                    <p className='text-foreground-default text-lg font-semibold'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.title'
                            defaultMessage='Bulk move transactions'
                            description='Title on the kiosk manage-spot bulk move transactions list screen'
                        />
                    </p>
                    <div className='border-border-default bg-background-surface flex w-full flex-col gap-2 rounded-xl border p-3'>
                        <div className='flex items-center justify-between gap-2'>
                            <label
                                htmlFor={`${ID}-stale-days`}
                                className='text-foreground-default text-sm font-medium'>
                                <FormattedMessage
                                    id='kiosk.manage-spot.bulk-move-transactions.stale-days.label'
                                    defaultMessage='Older than {days} days'
                                    description='Label for the stale transaction days slider on the bulk move list'
                                    values={{ days: viewProps.staleDays }}
                                />
                            </label>
                            <span className='text-brand-muted text-sm font-medium'>
                                {viewProps.minStaleDays}–{viewProps.maxStaleDays}
                            </span>
                        </div>
                        <input
                            id={`${ID}-stale-days`}
                            type='range'
                            min={viewProps.minStaleDays}
                            max={viewProps.maxStaleDays}
                            step={1}
                            value={viewProps.staleDays}
                            onChange={(event) => viewProps.setStaleDays(Number(event.target.value))}
                            className='accent-brand-primary w-full cursor-pointer'
                        />
                    </div>
                    <p className='text-brand-muted text-sm font-medium'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.selected-count'
                            defaultMessage='{count, plural, =0 {None selected} one {# selected} other {# selected}}'
                            description='Selected transaction count on the kiosk bulk move transactions list'
                            values={{ count: viewProps.selectedCount }}
                        />
                    </p>
                </div>

                {viewProps.isLoading && (
                    <div className='flex w-full flex-1 items-center justify-center py-8'>
                        <Spinner />
                    </div>
                )}
                {!viewProps.isLoading && viewProps.error && <div className='text-status-danger'>{viewProps.error}</div>}
                {!viewProps.isLoading && !viewProps.error && viewProps.transactions.length === 0 && (
                    <div className='text-secondary w-full py-6 text-center text-sm font-medium'>
                        <FormattedMessage
                            id='kiosk.manage-spot.bulk-move-transactions.empty'
                            defaultMessage='No transactions older than {days} days'
                            description='Empty state on the kiosk bulk move transactions list when no stale pending transactions exist'
                            values={{ days: viewProps.staleDays }}
                        />
                    </div>
                )}
                {!viewProps.isLoading && !viewProps.error && (
                    <div className='flex w-full flex-col gap-2'>
                        {viewProps.transactions.map((transaction) => (
                            <BulkMoveSelectableItem
                                key={transaction.id}
                                transaction={transaction}
                                selected={viewProps.selectedIds.has(transaction.id)}
                                onToggle={() => viewProps.toggleSelection(transaction.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
            <KioskNavigationFooter
                leftButton={
                    <DefaultSecondaryButton
                        text={CommonMessage.BUTTONS.GO_BACK}
                        onClick={viewProps.handleBack}
                    />
                }
                rightButton={
                    <div className='flex items-center gap-2'>
                        <DefaultSecondaryButton
                            text={
                                <FormattedMessage
                                    id='kiosk.manage-spot.bulk-move-transactions.remove'
                                    defaultMessage='Remove'
                                    description='Remove action button on the kiosk bulk move transactions list'
                                />
                            }
                            onClick={viewProps.handleRemove}
                            disabled={!viewProps.canRemove}
                        />
                        <DefaultPrimaryButton
                            text={
                                <FormattedMessage
                                    id='kiosk.manage-spot.bulk-move-transactions.move'
                                    defaultMessage='Move'
                                    description='Move action button on the kiosk bulk move transactions list'
                                />
                            }
                            onClick={viewProps.handleMove}
                            disabled={!viewProps.canMove}
                        />
                    </div>
                }
            />
        </div>
    );
};

export default BulkMoveTransactionsList;
